import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import { useTranslate } from 'core/hooks/useTranslate';
import { ROLES_GUID } from 'core/utils/user';
import type { IconName } from '../../../../icons/types';
import { getLabelWithContext } from './rowA11y';

type Item = ModuleItemType | SubmoduleItem;

type UseItemActionsParams = {
  item: Item;
  onDelete?: (item: Item) => void;
  onEdit?: (item: Item) => void;
  onToggleVisibility?: (item: Item) => void;
};

type UseItemActions = {
  canEditItem: boolean;
  deleteLabel: string;
  deleteTooltip: string;
  editLabel: string;
  editTooltip: string;
  handleDelete: () => void;
  handleEdit: () => void;
  handleToggleVisibility: () => void;
  visibilityIcon: IconName;
  visibilityLabel: string;
  visibilityTooltip: string;
};

function getItemName(item: Item): string | null | undefined {
  return 'section' in item ? item.lesson_name || item.section : item.lesson_name;
}

function useItemActions({ item, onDelete, onEdit, onToggleVisibility }: UseItemActionsParams): UseItemActions {
  const { t } = useTranslate();

  const isHidden = item.visibility === 0;
  const canEditItem = item.created_by?.role_guid === ROLES_GUID.TEACHER;
  const itemName = getItemName(item);

  const visibilityTooltip = isHidden ? t('organize-content:Show tooltip') : t('organize-content:Hide tooltip');
  const editTooltip = t('organize-content:Edit tooltip');
  const deleteTooltip = t('organize-content:Delete tooltip');

  function handleToggleVisibility(): void {
    onToggleVisibility?.(item);
  }

  function handleEdit(): void {
    onEdit?.(item);
  }

  function handleDelete(): void {
    onDelete?.(item);
  }

  return {
    canEditItem,
    deleteLabel: getLabelWithContext(t, deleteTooltip, itemName),
    deleteTooltip,
    editLabel: getLabelWithContext(t, editTooltip, itemName),
    editTooltip,
    handleDelete,
    handleEdit,
    handleToggleVisibility,
    visibilityIcon: isHidden ? 'visible-off' : 'visible',
    visibilityLabel: getLabelWithContext(t, visibilityTooltip, itemName),
    visibilityTooltip,
  };
}

export default useItemActions;
